import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Send, Users, Award, Briefcase } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BecomeAnAgentPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    city_state: '',
    is_licensed: '',
    experience_years: '',
    referral_source: '',
    message: ''
  });

  const requirements = [
    "18 years or older",
    "Eligible to work in the U.S.",
    "Willing to obtain life insurance license",
    "Strong communication skills",
    "Self-motivated and coachable"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/agents/apply`, form);
      setSubmitted(true);
      toast({
        title: "Application Submitted!",
        description: "We'll contact you within 48 hours.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-[#020617] min-h-screen pt-20">
        <section className="py-24">
          <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
            <div className="w-20 h-20 rounded-full bg-[#C5A059]/20 flex items-center justify-center mx-auto mb-8">
              <CheckCircle2 className="w-10 h-10 text-[#C5A059]" />
            </div>
            <h1 className="font-serif text-4xl text-white mb-6">Application Submitted!</h1>
            <p className="text-slate-400 mb-8">
              Thank you for your interest in joining ShopCompareInsure. Our team will review your application and contact you within 48 hours.
            </p>
            <Link
              to="/"
              className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all"
            >
              Return Home
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="become-agent-page">
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left - Info */}
            <div>
              <span className="text-[#C5A059] text-sm uppercase tracking-widest mb-4 block">
                Join Our Team
              </span>
              <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
                Become an Agent
              </h1>
              <p className="text-slate-300 text-lg mb-8">
                Start a career helping families protect their financial future. Join the ShopCompareInsure advisor network.
              </p>

              <div className="mb-12">
                <h3 className="text-white font-semibold mb-4">Requirements</h3>
                <ul className="space-y-3">
                  {requirements.map((req, idx) => (
                    <li key={idx} className="flex items-center space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-[#C5A059]" />
                      <span className="text-slate-400">{req}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-slate-500 text-sm mt-4">
                  Experience in sales or financial services is helpful but not required.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {[
                  { icon: Briefcase, label: "Flexible Career" },
                  { icon: Award, label: "Full Training" },
                  { icon: Users, label: "Team Support" }
                ].map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="bg-slate-900/50 border border-slate-800 p-4 text-center">
                      <Icon className="w-6 h-6 text-[#C5A059] mx-auto mb-2" />
                      <p className="text-slate-400 text-xs">{item.label}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right - Form */}
            <div className="bg-slate-900/30 border border-slate-800 p-8">
              <h2 className="font-serif text-2xl text-white mb-6">Agent Application Form</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="agent-application-form">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Full Name *</label>
                  <Input
                    type="text"
                    required
                    value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    className="bg-slate-950/50 border-slate-800 focus:border-[#C5A059] text-white h-12"
                    placeholder="Your full name"
                    data-testid="agent-form-name"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Email Address *</label>
                    <Input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="bg-slate-950/50 border-slate-800 focus:border-[#C5A059] text-white h-12"
                      placeholder="you@email.com"
                      data-testid="agent-form-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Phone Number *</label>
                    <Input
                      type="tel"
                      required
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="bg-slate-950/50 border-slate-800 focus:border-[#C5A059] text-white h-12"
                      placeholder="(555) 000-0000"
                      data-testid="agent-form-phone"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">City / State *</label>
                  <Input
                    type="text"
                    required
                    value={form.city_state}
                    onChange={(e) => setForm({ ...form, city_state: e.target.value })}
                    className="bg-slate-950/50 border-slate-800 focus:border-[#C5A059] text-white h-12"
                    placeholder="e.g., Dallas, TX"
                    data-testid="agent-form-location"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">Are You Currently Licensed? *</label>
                  <Select
                    value={form.is_licensed}
                    onValueChange={(value) => setForm({ ...form, is_licensed: value })}
                    required
                  >
                    <SelectTrigger className="bg-slate-950/50 border-slate-800 text-white h-12" data-testid="agent-form-licensed">
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="yes" className="text-slate-300">Yes – Life Insurance Licensed</SelectItem>
                      <SelectItem value="no" className="text-slate-300">No – Need Licensing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">Years of Sales or Financial Services Experience</label>
                  <Select
                    value={form.experience_years}
                    onValueChange={(value) => setForm({ ...form, experience_years: value })}
                  >
                    <SelectTrigger className="bg-slate-950/50 border-slate-800 text-white h-12" data-testid="agent-form-experience">
                      <SelectValue placeholder="Select experience level" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="none" className="text-slate-300">None</SelectItem>
                      <SelectItem value="1-2" className="text-slate-300">1–2 Years</SelectItem>
                      <SelectItem value="3-5" className="text-slate-300">3–5 Years</SelectItem>
                      <SelectItem value="5+" className="text-slate-300">5+ Years</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">How Did You Hear About Us?</label>
                  <Select
                    value={form.referral_source}
                    onValueChange={(value) => setForm({ ...form, referral_source: value })}
                  >
                    <SelectTrigger className="bg-slate-950/50 border-slate-800 text-white h-12" data-testid="agent-form-referral">
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="social_media" className="text-slate-300">Social Media</SelectItem>
                      <SelectItem value="friend_family" className="text-slate-300">Friend or Family</SelectItem>
                      <SelectItem value="current_agent" className="text-slate-300">Current ShopCompareInsure Agent</SelectItem>
                      <SelectItem value="online_search" className="text-slate-300">Online Search</SelectItem>
                      <SelectItem value="other" className="text-slate-300">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">Message / Comments</label>
                  <Textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="bg-slate-950/50 border-slate-800 focus:border-[#C5A059] text-white min-h-[100px]"
                    placeholder="Tell us about yourself and why you're interested..."
                    data-testid="agent-form-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all disabled:opacity-50"
                  data-testid="agent-form-submit"
                >
                  {loading ? 'Submitting...' : 'Submit Application'}
                  <Send className="w-4 h-4 ml-2" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BecomeAnAgentPage;
